import React, { useState, useEffect, useRef } from 'react';
import { Settings, GripVertical } from 'lucide-react';
import styles from '../styles/Dashboard.module.css';
import ActionBoxes from '../components/ActionBoxes/ActionBoxes';
import DailyQuote from '../components/DailyQuote/DailyQuote';
import UserLevel from '../components/UserLevel/UserLevel';
import LeaderboardPreview from '../components/Leaderboard/LeaderboardPreview';
import TongueTwisters from '../components/TongueTwisters/TongueTwisters';

const SECTIONS = [
    { id: 'quote', title: 'Daily Quote', subtitle: '' },
    { id: 'level', title: 'Level Progress', subtitle: 'Track your journey' },
    { id: 'leaderboard', title: 'Leaderboard Preview', subtitle: 'Monthly rewards' },
    { id: 'actions', title: 'Quick Actions', subtitle: 'Access your essential shortcuts' },
    { id: 'tongue_twisters', title: 'Tongue Twisters', subtitle: 'Practice tongue twisters' },
];

// Default order to use if localStorage is empty or invalid
const DEFAULT_SECTION_ORDER = ['quote', 'leaderboard', 'level', 'tongue_twisters', 'actions'];

const Dashboard = () => {
    const [sectionOrder, setSectionOrder] = useState(() => {
        try {
            const savedOrder = localStorage.getItem('dashboardSectionOrder');
            if (!savedOrder) return DEFAULT_SECTION_ORDER;

            const parsedOrder = JSON.parse(savedOrder);

            // Validate the saved order - make sure it includes all necessary sections
            const allSectionsIncluded = DEFAULT_SECTION_ORDER.every(
                section => parsedOrder.includes(section)
            );

            return allSectionsIncluded ? parsedOrder : DEFAULT_SECTION_ORDER;
        } catch (error) {
            console.error("Error loading dashboard layout:", error);
            return DEFAULT_SECTION_ORDER;
        }
    });

    const [isRearrangeOpen, setIsRearrangeOpen] = useState(false);
    const [draggedItem, setDraggedItem] = useState(null);
    const rearrangeRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (rearrangeRef.current && !rearrangeRef.current.contains(event.target)) {
                setIsRearrangeOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        try {
            localStorage.setItem('dashboardSectionOrder', JSON.stringify(sectionOrder));
        } catch (error) {
            console.error("Error saving dashboard layout:", error);
        }
    }, [sectionOrder]);

    const handleDragStart = (e, index) => {
        setDraggedItem(index);
        e.target.classList.add(styles.dragging);
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        if (draggedItem === null) return;

        const newOrder = [...sectionOrder];
        const item = newOrder[draggedItem];
        newOrder.splice(draggedItem, 1);
        newOrder.splice(index, 0, item);
        setSectionOrder(newOrder);
        setDraggedItem(index);
    };

    const handleDragEnd = (e) => {
        e.target.classList.remove(styles.dragging);
        setDraggedItem(null);
    };

    const renderSection = (sectionId) => {
        switch (sectionId) {
            case 'quote':
                return <DailyQuote />;
            case 'level':
                return <UserLevel />;
            case 'leaderboard':
                return <LeaderboardPreview />;
            case 'tongue_twisters':
                return <TongueTwisters />;
            case 'actions':
                return <ActionBoxes />;
            default:
                return null;
        }
    };

    // This ensures that all sections are displayed even if there's an issue with the saved order
    const displayAllSections = () => {
        const displayOrder = [...sectionOrder];

        // Add any missing sections to the end
        DEFAULT_SECTION_ORDER.forEach(section => {
            if (!displayOrder.includes(section)) {
                displayOrder.push(section);
            }
        });

        return displayOrder;
    };

    return (
        <div className={styles.container}>
            <main className={styles.main}>
                <div className={styles.header}>
                    <div ref={rearrangeRef} style={{ position: 'relative' }}>
                        <button
                            className={styles.rearrangeButton}
                            onClick={() => setIsRearrangeOpen(!isRearrangeOpen)}
                        >
                            <Settings size={16} />
                            <span>Rearrange Sections</span>
                        </button>

                        {isRearrangeOpen && (
                            <div className={styles.rearrangeMenu}>
                                {sectionOrder.map((sectionId, index) => {
                                    const section = SECTIONS.find(s => s.id === sectionId);
                                    if (!section) return null;
                                    return (
                                        <div
                                            key={sectionId}
                                            className={styles.rearrangeItem}
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, index)}
                                            onDragOver={(e) => handleDragOver(e, index)}
                                            onDragEnd={handleDragEnd}
                                        >
                                            <GripVertical size={16} />
                                            <span>{section.title}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                <div className={styles.masonryGrid}>
                    {displayAllSections().map(sectionId => (
                        <div key={sectionId} className={styles.gridItem}>
                            {renderSection(sectionId)}
                        </div>
                    ))}
                </div>
            </main>
        </div>
    );
};

export default Dashboard;